<template>
  <div id="start" class="home">
    <div class="home__header">
      <v-img contain :src="logo" class="partner-logo"></v-img>

      <mrx-translation
        v-if="hasStartTitle"
        :token="titleToken"
        class="home__title"
      ></mrx-translation>

    </div>

    <div class="home__actions">
      <action-card
        id="start-button"
        variant="dark"
        select-tracked
        :icon="icons.mdiPlus"
        title-token="app.form.button.start"
        description-token="app.home.start.description"
        @select="startAssessment()"
      ></action-card>

      <action-card
        v-if="isPpvEnabled"
        id="past-visits-button"
        variant="light"
        :icon="icons.mdiChartLine"
        title-token="ppv.page.title"
        @select="goToPastVisits()"
      ></action-card>

      <action-card
        v-if="showResumeEntryPoint"
        id="resume-encounter-button"
        variant="light"
        :icon="icons.mdiAccountGroup"
        title-token="app.nav.menu.encounter.resume.title"
        @select="goToPausedEncounters()"
      ></action-card>
    </div>

    <v-snackbar
      v-model="showSetupCompleteAlert"
      :timeout="5000"
      color="primaryblue"
      class="setup-complete-alert"
      top
      right
    >
      <div class="setup-complete-alert__content">
        <v-icon color="lightblue">{{ icons.mdiCheckCircleOutline }}</v-icon>
        <mrx-translation token="app.setup.complete_alert" />
      </div>

      <template #action="{ attrs }">
        <v-btn
          v-bind="attrs"
          :aria-label="$t('app.setup.complete_alert.dismiss')"
          color="lightblue"
          icon
          @click="showSetupCompleteAlert = false"
        >
          <v-icon>{{ icons.mdiClose }}</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import languageLogic from '@/mixins/languageLogic';
import icons from '@/mixins/icons';
import ActionCard from '@/components/widgets/ActionCard';
import { mapActions, mapState } from 'pinia';
import { useEncounterStore } from '@/stores/encounter';
import { usePartnerConfigStore } from '@/stores/partnerConfig';
import { useSavedEncountersStore } from '@/stores/savedEncounters';
import { useNavigation } from '@/composables/useNavigation';
import { resetEncounter } from '@/utils/encounterLifecycle';
import './styles/start.css';

export default {
  name: "Start",
  mixins: [languageLogic, icons],
  components: { ActionCard },
  data() {
    return {
      showSetupCompleteAlert: false,
      titleToken: 'app.header.title',
    };
  },
  setup() {
    const { next } = useNavigation();
    return { next };
  },
  computed: {
    hasStartTitle() {
      const translation = this.$t(this.titleToken)
      if (translation) {
        return translation !== this.titleToken
      } else {
        return false
      }
    },
    isPpvEnabled() {
      return this.$featureFlags.enablePPV
    },
    showResumeEntryPoint() {
      return this.hasSaved && Boolean(this.encounterPauseResume)
    },
    ...mapState(usePartnerConfigStore, ['logo', 'encounterPauseResume']),
    ...mapState(useSavedEncountersStore, ['hasSaved']),
  },
  methods: {
    showSetupCompleteMessage() {
      if (this.$route.query.setupCompleted !== 'true') return

      this.showSetupCompleteAlert = true
      const query = { ...this.$route.query }
      delete query.setupCompleted
      this.$router.replace({
        name: this.$route.name,
        params: this.$route.params,
        query,
      }).catch((error) => {
        if (error.name !== 'NavigationDuplicated') throw error
      })
    },
    startAssessment() {
      this.$track(this.$analyticsEvents.ASSESSMENT_STARTED, {
        component: 'Start',
        partner_logo: this.logo,
        has_custom_title: this.hasStartTitle
      })
      this.beginEncounter();
      this.next();
    },
    goToPastVisits() {
      this.$router.push({ name: 'PastPatientVisits' });
    },
    goToPausedEncounters() {
      this.$router.push({ name: 'Encounters' });
    },
    ...mapActions(useEncounterStore, { beginEncounter: 'begin' }),
  },
  mounted() {
    resetEncounter();
    this.showSetupCompleteMessage();
  },
};
</script>
